// Front Office daily collection and closing (PMS Phase 2 slice 7d) — mirrors FoClosingDtos.cs.

export type FoReceiptKind = 'ADVANCE_AT_CHECKIN' | 'INTERIM' | 'FINAL_SETTLEMENT' | 'REFUND';
export type FoClosingStatus = 'NONE' | 'DRAFT' | 'CLOSED' | 'VERIFIED' | 'REOPENED';

export interface FoReceiptRow {
  paymentId: string;
  paymentDate: string;
  receiptNumber?: string | null;
  paymentType: string;
  paymentMode: string;
  paymentReference?: string | null;
  bookingId: string;
  bookingNumber: string;
  guestName?: string | null;
  roomNumbers?: string | null;
  classification: FoReceiptKind;
  /** Refunds negative. */
  amount: number;
  isCash: boolean;
  receivedByName?: string | null;
  createdAt?: string | null;
}

export interface FoPaidOutRow {
  voucherId: string;
  voucherNumber: string;
  voucherDate: string;
  bookingNumber: string;
  guestName?: string | null;
  description: string;
  amount: number;
}

export interface FoSettlementRow {
  bookingId: string;
  bookingNumber: string;
  grcNumber?: string | null;
  guestName?: string | null;
  roomNumbers?: string | null;
  checkOutDate: string;
  bill: number;
  transferredToParty: number;
  partyNames?: string | null;
  advancesAdjusted: number;
  collectedAtCheckOut: number;
  collectedByMode?: string | null;
  refunded: number;
  outstanding: number;
  notYetInvoiced: number;
  status: 'SETTLED' | 'GUEST_OWES' | 'CREDIT_HELD' | 'NOT_INVOICED';
}

export interface FoModeLine {
  paymentMode: string;
  isCash: boolean;
  receiptCount: number;
  receipts: number;
  refunds: number;
  systemAmount: number;
  confirmedAmount?: number | null;
  confirmationRef?: string | null;
  difference: number;
  differenceReason?: string | null;
}

export interface FoDay {
  propertyId: string;
  businessDate: string;
  closingId?: string | null;
  status: FoClosingStatus;
  rowVersion: number;
  receipts: FoReceiptRow[];
  paidOuts: FoPaidOutRow[];
  settlements: FoSettlementRow[];
  modes: FoModeLine[];
  byClassification: Partial<Record<FoReceiptKind, number>>;
  totalReceipts: number;
  totalRefunds: number;
  unrecordedCount: number;
  openingFloat: number;
  openingFromPreviousClose: boolean;
  cashReceived: number;
  cashPaidOut: number;
  expectedCash: number;
  countedCash?: number | null;
  denominations?: Record<string, number> | null;
  cashDifference: number;
  differenceReason?: string | null;
  handedOverAmount: number;
  handedOverToName?: string | null;
  handedOverReference?: string | null;
  depositedAmount: number;
  depositBankAccountId?: string | null;
  depositSlipNumber?: string | null;
  retainedFloat: number;
  remarks?: string | null;
  closedAt?: string | null;
  closedByName?: string | null;
  verifiedAt?: string | null;
  verifiedByName?: string | null;
  verificationRemarks?: string | null;
  reopenedAt?: string | null;
  reopenedByName?: string | null;
  reopenReason?: string | null;
  countByDenomination: boolean;
  denominationValues: number[];
  cashModes: string[];
  propertyBusinessDate: string;
}

export interface FoModeConfirmation {
  paymentMode: string;
  confirmedAmount?: number | null;
  confirmationRef?: string;
  differenceReason?: string;
}

export interface FoCloseRequest {
  openingFloat?: number;
  countedCash?: number;
  denominations?: Record<string, number>;
  differenceReason?: string;
  handedOverAmount: number;
  handedOverToName?: string;
  handedOverReference?: string;
  depositedAmount: number;
  depositBankAccountId?: string;
  depositSlipNumber?: string;
  retainedFloat: number;
  modes: FoModeConfirmation[];
  remarks?: string;
  rowVersion: number;
}

export interface FoDaySummary {
  businessDate: string;
  receipts: number;
  refunds: number;
  cashReceived: number;
  status: FoClosingStatus;
  cashDifference?: number | null;
  handedOver?: number | null;
  deposited?: number | null;
  retained?: number | null;
  closedByName?: string | null;
  verifiedByName?: string | null;
}

export interface FoCollectionReport {
  from: string;
  to: string;
  receipts: FoReceiptRow[];
  paidOuts: FoPaidOutRow[];
  modes: FoModeLine[];
  byClassification: Partial<Record<FoReceiptKind, number>>;
  days: FoDaySummary[];
  totalReceipts: number;
  totalRefunds: number;
  cashPaidOut: number;
}

export const FO_RECEIPT_KIND_LABEL: Record<FoReceiptKind, string> = {
  ADVANCE_AT_CHECKIN: 'Advance at check-in',
  INTERIM: 'Interim advance',
  FINAL_SETTLEMENT: 'Final settlement',
  REFUND: 'Refund',
};

export const FO_STATUS_LABEL: Record<FoClosingStatus, { label: string; badge: string }> = {
  NONE: { label: 'Not closed', badge: 'badge-secondary' },
  DRAFT: { label: 'Draft', badge: 'badge-secondary' },
  CLOSED: { label: 'Closed', badge: 'badge-info' },
  VERIFIED: { label: 'Verified', badge: 'badge-success' },
  REOPENED: { label: 'Reopened', badge: 'badge-warning' },
};
